import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppSettings } from '../hooks/useAppSettings';
import Loading from './Loading';

// The guide's markup, styles and interactivity live as static assets under
// /user-guide/ (see frontend/public/user-guide/). Only the presentational
// HTML is reused here; the page itself is not directly servable (there is
// no index.html in that folder), so this route, gated by the admin check
// below, is the only way to reach it.
const GUIDE_ASSET_BASE = '/user-guide';

const GUIDE_HTML = `
<header class="topbar">
  <button class="topbar__menu-toggle" id="menuToggle" aria-label="Toggle navigation" aria-expanded="false">
    <span></span><span></span><span></span>
  </button>
  <div class="topbar__brand">
    <img class="topbar__logo topbar__logo--light" src="${GUIDE_ASSET_BASE}/assets/oi-logo.svg" alt="" width="147" height="50">
    <img class="topbar__logo topbar__logo--dark" src="${GUIDE_ASSET_BASE}/assets/oi-logo-dark.svg" alt="" width="147" height="50">
    <span class="topbar__divider" aria-hidden="true"></span>
    <span class="topbar__text">
      <span class="topbar__title">Trade App</span>
      <span class="topbar__subtitle">Admin Guide</span>
    </span>
  </div>
  <div class="topbar__actions">
    <div class="search">
      <svg viewBox="0 0 24 24" class="search__icon" aria-hidden="true"><path d="M10 2a8 8 0 105.29 14.02l4.85 4.84 1.41-1.41-4.84-4.85A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z"/></svg>
      <input type="search" id="searchInput" placeholder="Search the guide…" aria-label="Search the guide">
    </div>
    <button class="icon-btn" id="themeToggle" title="Toggle dark mode" aria-label="Toggle dark mode">
      <svg class="icon-sun" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4V2m0 20v-2m8-8h2M2 12h2m13.66-6.66l1.41-1.41M4.93 19.07l1.41-1.41M19.07 19.07l-1.41-1.41M4.93 4.93L6.34 6.34M12 7a5 5 0 100 10 5 5 0 000-10z"/></svg>
      <svg class="icon-moon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.742 13.045a8.088 8.088 0 01-2.077.267c-4.476 0-8.106-3.63-8.106-8.106 0-1.09.216-2.13.605-3.078A8.996 8.996 0 003 11.996C3 17.523 7.477 22 13.004 22a8.996 8.996 0 007.738-4.421 8.088 8.088 0 01-.001-4.534z"/></svg>
    </button>
    <button class="icon-btn" id="printBtn" title="Print or save as PDF" aria-label="Print or save as PDF">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z"/></svg>
    </button>
  </div>
</header>

<div class="layout">
  <nav class="sidebar" id="sidebar" aria-label="Guide sections">
    <div class="sidebar__scroll">
      <p class="sidebar__group-label">Start here</p>
      <ul>
        <li><a href="#overview" class="nav-link">How the app fits together</a></li>
      </ul>

      <p class="sidebar__group-label">Setting up your storefront</p>
      <ul>
        <li><a href="#storefront-setup" class="nav-link">Branding &amp; storefront setup</a></li>
        <li><a href="#product-catalogue" class="nav-link">Product catalogue</a></li>
        <li><a href="#search-filtering" class="nav-link">Search &amp; filtering</a></li>
      </ul>

      <p class="sidebar__group-label">Orders &amp; payments</p>
      <ul>
        <li><a href="#checkout" class="nav-link">Checkout</a></li>
        <li><a href="#payments-credit" class="nav-link">Payments &amp; credit</a></li>
        <li><a href="#confirmation-documents" class="nav-link">Confirmation &amp; documents</a></li>
      </ul>

      <p class="sidebar__group-label">Day to day</p>
      <ul>
        <li><a href="#admin-panel" class="nav-link">Self-service admin panel</a></li>
        <li><a href="#sales-tools" class="nav-link">Sales tools</a></li>
      </ul>

      <p class="sidebar__group-label">Reference</p>
      <ul>
        <li><a href="#integrations" class="nav-link">Optional integrations</a></li>
        <li><a href="#roadmap" class="nav-link">On the roadmap</a></li>
        <li><a href="#glossary" class="nav-link">Glossary</a></li>
        <li><a href="#help" class="nav-link">Getting help</a></li>
      </ul>
    </div>
  </nav>

  <div class="sidebar-backdrop" id="sidebarBackdrop"></div>

  <main id="admin-guide-main">

    <div class="legend" id="legend">
      <span class="legend__label">Filter by:</span>
      <button class="chip chip--all is-active" data-filter="all">Everything</button>
      <button class="chip chip--self" data-filter="self">✏️ I can do this myself</button>
      <button class="chip chip--vintner" data-filter="vintner">🗄️ Managed in Vintner</button>
      <button class="chip chip--oneoff" data-filter="oneoff">✉️ One-off setup (ask us)</button>
      <button class="chip chip--auto" data-filter="auto">✅ Automatic</button>
    </div>

    <p class="no-results" id="noResults" hidden>Nothing in the guide matches that search.</p>

    <!-- OVERVIEW -->
    <section id="overview" class="content-section">
      <h1>How the Trade App fits together</h1>
      <p class="lede">The Trade App is best thought of as a window onto <strong>Vintner</strong>, not a separate system in its own right. Every product, price, stock level, customer account and promotion you see on the app is read live from Vintner, and every order placed on the app is written straight back into Vintner. The app's job is to present all of that through a fast, modern, branded ordering experience for your trade customers, rather than holding its own separate copy of your business data.</p>
      <p>That has a practical consequence worth keeping in mind throughout this guide. Most of what shapes your day-to-day use of the app, things like your customers, their delivery days, their credit terms, your promotions and your stock, is managed in Vintner the same way it always has been. What the app itself controls is <em>how</em> that information is presented, and which of its many optional features are switched on.</p>
      <p>This guide breaks every one of those features out individually, grouped by where you'll meet them, so you can jump straight to the thing you're wondering about. Each one is tagged with who looks after it:</p>
      <ul class="legend-key">
        <li><span class="badge badge--self">✏️ Self-service</span>: you configure this yourself, right now, in the app's admin panel.</li>
        <li><span class="badge badge--vintner">🗄️ Vintner</span>: managed in Vintner, exactly as you already do today, often per customer account.</li>
        <li><span class="badge badge--oneoff">✉️ One-off setup</span>: a configuration option we switch on for you. Send us the details once and we'll build it in.</li>
        <li><span class="badge badge--auto">✅ Automatic</span>: built into the app for every customer, with nothing to configure at all.</li>
      </ul>
      <p>Use the <strong>Filter by</strong> options above, or the search box, to narrow this page down to just what's relevant to you.</p>
    </section>

    <!-- STOREFRONT SETUP -->
    <section id="storefront-setup" class="content-section">
      <h2>Branding &amp; storefront setup</h2>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Branding and personalisation</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Your logo, brand colours (main colour, link colour, hover colour, and colours for notices and errors), company name, address, phone number, VAT number, browser tab title, and homepage welcome message all appear throughout the app, along with footer branding. Your Contact Us page is built automatically from these same company details.</p>
        <p>A few smaller personalisation touches live here too: the currency symbol shown throughout the app, the label used for your product list page (for example "My Wines" instead of "My Products"), and whether the Open Imagination / Vintner footer logos are shown at all.</p>
        <p class="detail"><strong>How it works:</strong> send us your logo and brand colours and we'll build them into your site. We do this once, as part of setting your storefront up, rather than as something you change day to day. Just let us know any time you want your branding updated.</p>
      </article>

      <article class="feature-card" data-badges="oneoff auto">
        <div class="feature-card__head">
          <h3>Secure customer login</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Customers sign in through Auth0, an industry-standard secure login service, so we never handle or store passwords directly.</p>
        <p class="detail"><strong>How it works:</strong> we set this up once, when your storefront is first built.</p>
      </article>

      <article class="feature-card" data-badges="oneoff vintner">
        <div class="feature-card__head">
          <h3>Trade account applications</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--vintner">Vintner</span></div>
        </div>
        <p>A public "apply for a trade account" form lets prospective customers submit their details, which creates a prospect record in Vintner and emails your team the application. The notification can go to a dedicated mailbox with its own subject line and message, separate from your general order-confirmation address. If you take payment by direct debit, the applicant can also automatically receive a mandate form to sign and return.</p>
        <p class="detail"><strong>How it works:</strong> we switch the form on and the wording is yours to set (we're happy to help draft it). If you'd like the direct debit mandate sent out automatically, send us your mandate template and we'll wire it in.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Privacy policy &amp; terms and conditions</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Your Privacy Policy and Terms and Conditions pages carry your own wording, not generic placeholder text.</p>
        <p class="detail"><strong>How it works:</strong> send us the text for each and we'll build it into your site as part of setup.</p>
      </article>
    </section>

    <!-- PRODUCT CATALOGUE -->
    <section id="product-catalogue" class="content-section">
      <h2>Product catalogue</h2>

      <article class="feature-card" data-badges="vintner auto">
        <div class="feature-card__head">
          <h3>Your catalogue and customer-specific lists</h3>
          <div class="badges"><span class="badge badge--vintner">Vintner</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Customers can either browse your full public catalogue, or be shown only their own curated list: the specific wines you've decided that account can see and buy.</p>
        <p class="detail"><strong>How it works:</strong> you manage this entirely in Vintner. Build and maintain each customer's own list there as you always have, and the app just reflects it, with nothing to configure on our side.</p>
      </article>

      <article class="feature-card" data-badges="oneoff vintner">
        <div class="feature-card__head">
          <h3>Cases, bottles, casks and kegs</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--vintner">Vintner</span></div>
        </div>
        <p>You can sell in bottles, cases, or both. Products above a certain unit size are also automatically labelled "Cask" or "Keg", and individual products can be restricted to fixed order multiples (for example, only sold in sixes).</p>
        <p class="detail"><strong>How it works:</strong> we make the cases-only / bottles-only / both choice once, and turn cask/keg labelling on, as part of setup. You set multiples-of-X rules per product in Vintner.</p>
      </article>

      <article class="feature-card" data-badges="oneoff auto">
        <div class="feature-card__head">
          <h3>Stock control and backorders</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>You can cap what a customer can order at your live Vintner stock level, or leave ordering open regardless of stock. A separate setting lets customers order out-of-stock lines onto backorder rather than being blocked outright, and you can choose whether "in stock" or "on order" messaging shows at all, including an optional "Low Stock" badge once remaining stock drops below a case threshold you set.</p>
        <p class="detail"><strong>How it works:</strong> we switch this on as a one-off choice, including your preferred low-stock threshold. Once it's on, the numbers always come live from your Vintner stock figures, so you have nothing extra to maintain.</p>
      </article>

      <article class="feature-card" data-badges="oneoff vintner auto">
        <div class="feature-card__head">
          <h3>Product page fields</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--vintner">Vintner</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Each field on a product page can be switched on or off independently, so pages only show what's actually relevant to your range:</p>
        <div class="tag-list">
          <span class="tag">Tasting notes</span>
          <span class="tag">Winemaking notes</span>
          <span class="tag">Serving suggestion</span>
          <span class="tag">Up to 4 custom note fields</span>
          <span class="tag">ABV %</span>
          <span class="tag">Allergens</span>
          <span class="tag">Organic badge</span>
          <span class="tag">Biodynamic badge</span>
          <span class="tag">Excise description</span>
          <span class="tag">Country / region</span>
          <span class="tag">Wine type</span>
          <span class="tag">Vintage</span>
          <span class="tag">Winemaker name</span>
          <span class="tag">Bin / location number</span>
        </div>
        <p class="detail"><strong>How it works:</strong> we switch on the fields you want as part of setup, including custom titles for your four general note fields. The content behind them is entered by you in Vintner as usual. Once tasting notes are in place, every product page automatically gets a one-click branded tasting notes PDF download, with nothing extra to configure.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Product images</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Product photos can be sourced a few different ways depending on how your product data is set up: served directly from Vintner, pulled from an external image URL you host elsewhere, or resolved from a JSON image manifest.</p>
        <p class="detail"><strong>How it works:</strong> we pick the approach that matches how your product images are currently stored, as part of setup. It's a technical choice with no ongoing maintenance for you.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Default catalogue order</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>The order products appear in when a customer first lands on their product list (before they apply their own sort) has a default we set for your storefront, for example grouped by product group.</p>
        <p class="detail"><strong>How it works:</strong> tell us how you'd like new visitors' catalogue to be ordered by default, and we'll set it. Customers can still re-sort using the Sort dropdown at any time.</p>
      </article>
    </section>

    <!-- SEARCH & FILTERING -->
    <section id="search-filtering" class="content-section">
      <h2>Search &amp; filtering</h2>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Search filters</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Customers can narrow the catalogue using any combination of: country, product group, super group, colour/type, closure, and organic/biodynamic. Each filter can be switched on independently, and each has a custom label you can set. For example, you could relabel "Type" as "Colour" if that fits your range better.</p>
        <p class="detail"><strong>How it works:</strong> tell us which filters you want shown, and what you'd like each one labelled, and we'll switch them on. The values behind each filter come from your product categorisation in Vintner.</p>
      </article>

      <article class="feature-card" data-badges="auto">
        <div class="feature-card__head">
          <h3>Live search &amp; autocomplete</h3>
          <div class="badges"><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>As a customer types into the search box, matching products are found automatically (search updates a moment after typing stops, or immediately on pressing Enter), and the dedicated Product Search page also offers autocomplete suggestions as they type.</p>
        <p class="detail"><strong>How it works:</strong> this runs automatically for every storefront, with nothing to configure.</p>
      </article>

      <article class="feature-card" data-badges="auto">
        <div class="feature-card__head">
          <h3>Sort and page size</h3>
          <div class="badges"><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Every customer can sort their product list (Default, A–Z, Z–A, Price low–high, Price high–low) and choose how many products are shown per page (10, 20, 50, or all).</p>
        <p class="detail"><strong>How it works:</strong> these controls are built into every storefront and don't need switching on.</p>
      </article>

      <article class="feature-card" data-badges="auto">
        <div class="feature-card__head">
          <h3>Bulk Add</h3>
          <div class="badges"><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Customers can enter quantities against several products on their list, then click <strong>Bulk Add</strong> once to add all of them to the basket in a single action, rather than clicking "Add to Cart" line by line.</p>
        <p class="detail"><strong>How it works:</strong> this is built into the product list for every storefront, with nothing to configure.</p>
      </article>
    </section>

    <!-- CHECKOUT -->
    <section id="checkout" class="content-section">
      <h2>Checkout</h2>

      <article class="feature-card" data-badges="vintner auto">
        <div class="feature-card__head">
          <h3>Delivery address &amp; notes</h3>
          <div class="badges"><span class="badge badge--vintner">Vintner</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Customers pick from their available delivery addresses at checkout. If a customer has more than one address set up in Vintner, they're prompted to choose which one this order should go to. A free-text Delivery Notes field is always available for special instructions.</p>
        <p class="detail"><strong>How it works:</strong> you manage a customer's delivery addresses in Vintner; the app automatically shows a picker whenever there's more than one.</p>
      </article>

      <article class="feature-card" data-badges="vintner oneoff">
        <div class="feature-card__head">
          <h3>Delivery days, collection &amp; cut-off times</h3>
          <div class="badges"><span class="badge badge--vintner">Vintner</span><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Customers can choose from their available delivery days at checkout, or collect their order in person instead. You can also show a warning when someone orders for next-day delivery after your cut-off time, for example: <em>"Orders placed after 3pm cannot be guaranteed for delivery next day."</em></p>
        <p class="detail"><strong>How it works:</strong> you set delivery days per customer in Vintner. If a customer has none configured there, none will show on the app. We switch collection on (and need your company name, collection address and opening hours on file), and set your cut-off time and wording as a one-off choice.</p>
      </article>

      <article class="feature-card" data-badges="vintner auto">
        <div class="feature-card__head">
          <h3>Customer Reference / Purchase Order</h3>
          <div class="badges"><span class="badge badge--vintner">Vintner</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Every checkout has a Customer Reference / Purchase Order field for the customer to fill in. For customers who need to quote a PO number on every order, you can mark their Vintner account as requiring it. The field then shows an asterisk, and checkout is blocked with "Please enter a Customer Reference / Purchase Order" until they fill it in.</p>
        <p class="detail"><strong>How it works:</strong> the field itself is always there for every customer, automatically. Whether it's <em>required</em> is a flag on that customer's own record in Vintner, so you control it account by account.</p>
      </article>

      <article class="feature-card" data-badges="oneoff auto">
        <div class="feature-card__head">
          <h3>Minimum order values</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>You can require a global minimum basket value and/or a minimum bottle quantity before checkout is allowed, and force ordering in fixed multiples where relevant. You can also set a minimum basket value per Vintner customer. Kegs and bag-in-box products are automatically exempted from the minimum-bottle-quantity rule, since they're not sold by the bottle.</p>
        <p class="detail"><strong>How it works:</strong> tell us the thresholds you want and we'll set them. The keg and bag-in-box exemption is already built in.</p>
      </article>

      <article class="feature-card" data-badges="vintner oneoff">
        <div class="feature-card__head">
          <h3>Promotions</h3>
          <div class="badges"><span class="badge badge--vintner">Vintner</span><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>You can run volume discounts, percentage-off deals, buy-one-get-one style offers and free-product promotions, each with its own date range and an optional message shown to the customer at checkout.</p>
        <p class="detail"><strong>How it works:</strong> you build and manage these entirely in Vintner, the same way you already run promotions. Just let us know so we can set up the background sync that keeps the app in step with Vintner: while the promotions themselves always live in Vintner, the app keeps a local cache of them to keep checkout fast. Once that sync is running, you don't need to do anything else.</p>
      </article>

      <article class="feature-card" data-badges="oneoff vintner">
        <div class="feature-card__head">
          <h3>Stock reserves at checkout</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--vintner">Vintner</span></div>
        </div>
        <p>If a customer has stock specifically reserved or allocated for them in Vintner (for example, an en primeur allocation), checkout can automatically draw down from that reservation first, splitting each order line between "from reserves" and "from general stock" as needed.</p>
        <p class="detail"><strong>How it works:</strong> we switch this on as a one-off choice for storefronts that use reserved stock. The reservations themselves are managed in Vintner exactly as today.</p>
      </article>
    </section>

    <!-- PAYMENTS & CREDIT -->
    <section id="payments-credit" class="content-section">
      <h2>Payments &amp; credit</h2>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Card payment</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Customers can pay by card through Stripe at checkout, with payments settling directly into your own Stripe account.</p>
        <p class="detail"><strong>How it works:</strong> send us your Stripe account keys and we'll wire card payment in.</p>
      </article>

      <article class="feature-card" data-badges="oneoff vintner">
        <div class="feature-card__head">
          <h3>Pay on account (trade credit)</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--vintner">Vintner</span></div>
        </div>
        <p>Customers can pay against their trade credit balance instead of by card. Whether this option appears is controlled by up to three layers: a master on/off switch for the whole storefront, whether the individual customer's Vintner record is flagged as credit-eligible, and, optionally, whether the option is hidden entirely once their available credit is lower than the order total (rather than shown with a warning).</p>
        <p class="detail"><strong>How it works:</strong> we turn pay-on-account on as a one-off choice and configure which of those layers apply. Whether a specific customer is credit-eligible stays managed in Vintner as normal.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Allow ordering over a credit limit</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>By default, a customer whose order would take them over their available credit simply can't check out with pay-on-account. You can switch on a softer approach instead: the order is still allowed through, but the customer sees <em>"This order will exceed your credit limit. Your order will be reviewed and a member of the team will contact you."</em> That flags the order for your team to follow up on, rather than blocking the sale outright.</p>
        <p class="detail"><strong>How it works:</strong> this is a single storefront-wide on/off switch we set for you. It's a soft warning at checkout. Real credit control still happens in Vintner and with your team.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Nudging customers toward one payment method</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>If you'd rather customers used their trade credit than paid by card, you can hide the card option automatically whenever a customer already has enough available credit to cover the order. Card payment still shows for anyone without enough credit.</p>
        <p class="detail"><strong>How it works:</strong> tell us if you'd like this preference switched on. It's a one-off choice, not something customers or you adjust day to day.</p>
      </article>

      <article class="feature-card" data-badges="vintner auto">
        <div class="feature-card__head">
          <h3>Sub-account billing</h3>
          <div class="badges"><span class="badge badge--vintner">Vintner</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>If you use parent/child ("sub-account") structures in Vintner, for example one head-office billing account with several site-level ordering accounts, orders are always placed and recorded against the sub-account the customer is logged into. Payment is always collected against the parent account. This isn't a configurable choice; it's fixed behaviour whenever a parent/child relationship exists.</p>
        <p class="detail"><strong>How it works:</strong> you set up the parent/child relationship in Vintner as normal, via the parent account's code on the child record. The app automatically routes payment to the parent account for you, with nothing to configure on our side.</p>
      </article>

      <article class="feature-card" data-badges="auto">
        <div class="feature-card__head">
          <h3>Settling a balance from My Account</h3>
          <div class="badges"><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Customers with an outstanding balance can settle some or all of it by card directly from their My Account page, separate from placing an order.</p>
        <p class="detail"><strong>How it works:</strong> this uses the same Stripe setup as checkout, so there's nothing extra to configure once card payment is switched on.</p>
      </article>
    </section>

    <!-- CONFIRMATION & DOCUMENTS -->
    <section id="confirmation-documents" class="content-section">
      <h2>Confirmation &amp; documents</h2>

      <article class="feature-card" data-badges="oneoff auto">
        <div class="feature-card__head">
          <h3>Order confirmation emails</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Customers automatically receive an email confirming their order once it's placed. Your sales team's own email address is copied in on every confirmation too.</p>
        <p class="detail"><strong>How it works:</strong> we set this up once (mailbox, sender name, reply-to address). It then runs automatically in the background, and you don't need to do anything further.</p>
      </article>

      <article class="feature-card" data-badges="oneoff vintner">
        <div class="feature-card__head">
          <h3>VAT invoices</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--vintner">Vintner</span></div>
        </div>
        <p>Every order has a downloadable PDF invoice. Adding your VAT number also controls whether Vintner's own invoice number line appears on it at all. Without a VAT number on file, that line is left off entirely.</p>
        <p class="detail"><strong>How it works:</strong> send us your VAT number to have both it and the invoice number appear on invoices, and make sure Vintner is set up to generate invoice numbers on your orders.</p>
      </article>

      <article class="feature-card" data-badges="auto">
        <div class="feature-card__head">
          <h3>Order history and re-ordering</h3>
          <div class="badges"><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Every customer has a My Orders page listing their past orders. Each one can be opened for full details, printed straight from the browser, or re-ordered with a single click that adds every line from that order back into the basket in one go.</p>
        <p class="detail"><strong>How it works:</strong> this works automatically for every account, with nothing to configure.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Accounting codes on orders</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Orders sent through to Vintner can carry a nominal (bookkeeping) code and a sales-operator code, so they land in the right place in your accounts and reporting without any manual re-coding.</p>
        <p class="detail"><strong>How it works:</strong> tell us the codes to use and we'll attach them to every order automatically. It's a one-off setup step with nothing ongoing for you to do.</p>
      </article>
    </section>

    <!-- ADMIN PANEL -->
    <section id="admin-panel" class="content-section">
      <h2>Self-service admin panel</h2>
      <p>Three tools live behind an admin login inside the app itself. The one thing we need from you is a list of the email addresses your team wants granted admin access. Once that's set up, everything below is entirely in your hands, with no need to come back to us.</p>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Getting admin access</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Send us the list of email addresses that should have admin rights. Once we've added them, signing in with one of those addresses shows extra icons next to your profile: a <strong>gear</strong> (Trade App Settings, which also holds the metrics dashboard), a <strong>people</strong> icon (Customer Lookup), and a <strong>book</strong> icon that opens this guide. Those three icons are your entry point to everything on this page.</p>
        <figure class="screenshot">
          <img src="${GUIDE_ASSET_BASE}/assets/screens/profile-bar.png" alt="The admin icon row next to the profile name: a gear, a people icon and a book icon" loading="lazy">
          <figcaption>These icons only appear once your account has been given admin access.</figcaption>
        </figure>
      </article>

      <article class="feature-card" data-badges="self">
        <div class="feature-card__head">
          <h3>Homepage banner</h3>
          <div class="badges"><span class="badge badge--self">Self-service</span></div>
        </div>
        <p>Display a banner above the page title on every page, either a simple image or embedded HTML such as an Elfsight widget. Handy for things like an extra call to action.</p>
        <ol class="steps">
          <li>Click the <strong>gear icon</strong> next to your profile to open <strong>Trade App Settings</strong>.</li>
          <li>Under <strong>Banner</strong>, choose one of two options:
            <ul>
              <li><strong>Option 1, HTML code:</strong> paste only the HTML content part of the widget (for Elfsight, ignore the code inside the <code>&lt;script&gt;</code> tags).</li>
              <li><strong>Option 2, Image:</strong> upload a JPG, PNG, GIF or WEBP image up to 2MB. A wide, short image works best (around 1200×200px).</li>
            </ul>
          </li>
          <li>If both are filled in, the HTML code is shown and the image is ignored.</li>
          <li>Click <strong>Update Banner</strong>, and the banner appears immediately across the site.</li>
        </ol>
        <figure class="screenshot">
          <img src="${GUIDE_ASSET_BASE}/assets/screens/admin-banner.png" alt="The Banner section of Trade App Settings, showing the HTML code option and the image upload option" loading="lazy">
          <figcaption>The Banner section of Trade App Settings.</figcaption>
        </figure>
      </article>

      <article class="feature-card" data-badges="self">
        <div class="feature-card__head">
          <h3>Sales metrics dashboard</h3>
          <div class="badges"><span class="badge badge--self">Self-service</span></div>
        </div>
        <p>A simple dashboard shows average order value, order count, and unique logged-in users over any date range you choose.</p>
        <ol class="steps">
          <li>Open <strong>Trade App Settings</strong> (gear icon) and scroll down to <strong>Trade App Metrics</strong>.</li>
          <li>Pick a <strong>From</strong> and <strong>To</strong> date. It defaults to the start of the current month through to today.</li>
          <li>The figures for <strong>AVG Order total</strong>, <strong>Orders number</strong> and <strong>Unique logged in users</strong> update for the range you've chosen.</li>
        </ol>
      </article>

      <article class="feature-card" data-badges="self">
        <div class="feature-card__head">
          <h3>Customer lookup and ordering on a customer's behalf</h3>
          <div class="badges"><span class="badge badge--self">Self-service</span></div>
        </div>
        <p>Your team can search and browse your trade customers, and place an order as them. This is useful when a customer phones or emails in an order rather than using the app themselves.</p>
        <ol class="steps">
          <li>Click the <strong>people icon</strong> next to your profile (or the <strong>Order</strong> menu item) to open <strong>Customer Lookup</strong>.</li>
          <li>Click <strong>Load All Customers</strong>. This fetches your full customer list in batches, so it can take a few seconds.</li>
          <li>Use <strong>Filter Results</strong> to search by account code, email or name, or use <strong>Clear Filters</strong> to reset.</li>
        </ol>
        <figure class="screenshot">
          <img src="${GUIDE_ASSET_BASE}/assets/screens/customer-lookup.png" alt="Customer Lookup showing the filter box and a table of matching customers, each with an arrow button to switch into their account" loading="lazy">
          <figcaption>Customer Lookup after loading your customer list.</figcaption>
        </figure>
        <ol class="steps" start="4">
          <li>Click the arrow button in the <strong>Action</strong> column of a customer's row to switch into their account. You'll land on the product list "viewing as" that customer.</li>
          <li>Browse and place the order exactly as the customer would.</li>
          <li>When you're done, click the <strong>house icon</strong> ("Switch back to your account") next to your profile to return to your own admin login.</li>
        </ol>
        <figure class="screenshot">
          <img src="${GUIDE_ASSET_BASE}/assets/screens/viewing-as.png" alt="The header showing 'Viewing as: Gerrard Godwin | Logged in as: John Smith' with a house icon to switch back" loading="lazy">
          <figcaption>While you're browsing on a customer's behalf, the header always shows who you're viewing as, and gives you a one-click way back.</figcaption>
        </figure>
      </article>
    </section>

    <!-- SALES TOOLS -->
    <section id="sales-tools" class="content-section">
      <h2>Sales tools</h2>

      <article class="feature-card" data-badges="oneoff auto">
        <div class="feature-card__head">
          <h3>RRP calculator</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--auto">Automatic</span></div>
        </div>
        <p>Customers can enter their desired margin and see the recommended retail price worked out from your wholesale cost, VAT included. It's hidden by default each time a customer visits. They switch it on themselves using the small toggle next to Customer Lookup in the header, so it never clutters the page for customers who don't want it.</p>
        <p class="detail"><strong>How it works:</strong> we turn this on with a one-off switch. You don't need to do anything ongoing. Each customer enters their own margin and shows or hides the tool themselves as they browse.</p>
      </article>

      <article class="feature-card" data-badges="oneoff vintner">
        <div class="feature-card__head">
          <h3>Account manager details ("My Rep")</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span><span class="badge badge--vintner">Vintner</span></div>
        </div>
        <p>Customers can see their named account manager's name, phone number, email address, and LinkedIn profile in a popup from their profile menu.</p>
        <p class="detail"><strong>How it works:</strong> we switch this on as part of setup. Keep the account manager fields up to date on each customer's record in Vintner, and the app will simply display whatever is there.</p>
      </article>
    </section>

    <!-- INTEGRATIONS -->
    <section id="integrations" class="content-section">
      <h2>Optional integrations (available on request)</h2>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>HubSpot</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>If you use HubSpot, we can match your HubSpot company records to the right Vintner trade account using your Vintner customer code, and assign a specific HubSpot owner to those matched records. Worth knowing upfront: this is a lightweight, one-way lookup rather than a full contact or deal sync. If you need deeper HubSpot integration, let us know and we can scope that as a separate piece of work.</p>
        <p class="detail"><strong>How it works:</strong> send us a HubSpot API token, confirm the customer code field you use, and let us know which owner records should be assigned to, and we'll set it up.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Website analytics (Google Analytics)</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>We can wire in Google Analytics for site-wide visit tracking.</p>
        <p class="detail"><strong>How it works:</strong> send us your Google Analytics ID and we'll add it.</p>
      </article>

      <article class="feature-card" data-badges="oneoff">
        <div class="feature-card__head">
          <h3>Spam protection (reCAPTCHA)</h3>
          <div class="badges"><span class="badge badge--oneoff">One-off setup</span></div>
        </div>
        <p>Google reCAPTCHA can be added to cut down spam submissions. It's currently scoped to the public trade account application form.</p>
        <p class="detail"><strong>How it works:</strong> send us your reCAPTCHA site ID and we'll add it.</p>
      </article>
    </section>

    <!-- ROADMAP -->
    <section id="roadmap" class="content-section">
      <h2>On the roadmap</h2>
      <p>Features we're actively building. Not available yet, but worth knowing about.</p>

      <article class="feature-card" data-badges="roadmap">
        <div class="feature-card__head">
          <h3>Vendor sales reports</h3>
          <div class="badges"><span class="badge badge--roadmap">Coming soon</span></div>
        </div>
        <p>We're working on an automated report sent to your sales team, summarising trade app activity week by week: total orders, total order value, and average order value, presented graphically alongside a comparison with the previous period.</p>
      </article>

      <article class="feature-card" data-badges="roadmap">
        <div class="feature-card__head">
          <h3>Custom invoice templates</h3>
          <div class="badges"><span class="badge badge--roadmap">Coming soon</span></div>
        </div>
        <p>Every vendor currently uses the same fixed invoice layout. Custom-branded invoice templates are something we can build on request.</p>
      </article>
    </section>

    <!-- GLOSSARY -->
    <section id="glossary" class="content-section">
      <h2>Glossary</h2>
      <dl class="glossary">
        <dt>Vintner</dt>
        <dd>The trade ordering system that holds your live business data: products, prices, stock, customers and promotions. The Trade App is the customer-facing storefront layered on top of it.</dd>

        <dt>Trade account / trade customer</dt>
        <dd>A business customer account set up in Vintner with its own credit terms, delivery days, product list and pricing.</dd>

        <dt>Parent / child (sub-)account</dt>
        <dd>A billing structure where several ordering accounts (children) roll up to one main account (parent), for example a head office and its branches. Orders are placed and recorded against the child account, but payment is always collected against the parent.</dd>

        <dt>Backorder</dt>
        <dd>An order line placed against an item that's currently out of stock, to be fulfilled once stock arrives, rather than being blocked at checkout.</dd>

        <dt>Stock reserve / allocation</dt>
        <dd>Stock set aside for a specific customer in Vintner (for example, an en primeur allocation), which checkout can draw down from before touching general stock.</dd>

        <dt>Pay on account</dt>
        <dd>Paying against a customer's trade credit balance, rather than by card at checkout.</dd>

        <dt>Customer Reference / Purchase Order</dt>
        <dd>A free-text field at checkout for the customer's own reference or PO number. Some customers can be set to require it before they can complete an order.</dd>

        <dt>Admin user</dt>
        <dd>A team member whose login has been granted the "admin" role. This unlocks Trade App Settings, the sales metrics dashboard, and Customer Lookup / order-on-behalf.</dd>

        <dt>Prospect</dt>
        <dd>A record created in Vintner when someone submits the public "apply for a trade account" form, before they become a full trade customer.</dd>
      </dl>
    </section>

    <!-- HELP -->
    <section id="help" class="content-section">
      <h2>Getting help</h2>
      <p>This guide covers what the Trade App can do and who looks after each part of it. If you need something changed that's marked <span class="badge badge--oneoff">One-off setup</span> above, or you've spotted something that doesn't match what you see in your app, get in touch with your account manager, or use the <strong>Contact Us</strong> page in the app's footer.</p>
    </section>

    <footer class="page-footer">
      <img class="page-footer__mark page-footer__mark--light" src="${GUIDE_ASSET_BASE}/assets/oi-mark-mono-ink.svg" alt="" width="20" height="27">
      <img class="page-footer__mark page-footer__mark--dark" src="${GUIDE_ASSET_BASE}/assets/oi-mark-mono-reverse.svg" alt="" width="20" height="27">
      <p>This guide is built and supported by <a href="https://www.openimagination.co.uk" target="_blank" rel="noopener noreferrer">Open Imagination</a>, the team behind your Trade App storefront.</p>
    </footer>

  </main>
</div>
`;

export default function AdminGuide() {
  const settings = useAppSettings();
  const history = useHistory();
  const isAdmin = !settings['__empty'] && settings['__is_admin_user'] === true;

  // Same admin gate as AdminSettings.js / OrderForCustomer.js: anyone not
  // flagged as an admin by the backend is sent back to the storefront.
  useEffect(() => {
    if (!settings['__empty'] && !settings['__is_admin_user']) {
      history.push('/product-list');
    }
  }, [settings, history]);

  useEffect(() => {
    if (!isAdmin) return;

    const previousTitle = document.title;
    document.title = 'Trade App Admin Guide';

    const head = document.head;
    const createdLinks = [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
      { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600&display=swap' },
      { rel: 'stylesheet', href: `${GUIDE_ASSET_BASE}/styles.css` },
    ].map((attrs) => {
      const link = document.createElement('link');
      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'crossOrigin') {
          link.crossOrigin = value;
        } else {
          link.setAttribute(key, value);
        }
      });
      head.appendChild(link);
      return link;
    });

    const script = document.createElement('script');
    script.src = `${GUIDE_ASSET_BASE}/script.js`;
    document.body.appendChild(script);

    return () => {
      document.title = previousTitle;
      createdLinks.forEach((link) => head.removeChild(link));
      document.body.removeChild(script);
    };
  }, [isAdmin]);

  // The storefront's own header and profile bar are position: fixed, not
  // part of normal document flow, so the guide's sticky topbar/sidebar
  // need to know their real rendered height to offset below them instead
  // of overlapping them while scrolling.
  useEffect(() => {
    if (!isAdmin) return;

    function measureChromeHeight() {
      const chrome = document.querySelector('.customer-profile') || document.querySelector('.header');
      const height = chrome ? Math.ceil(chrome.getBoundingClientRect().bottom) : 0;
      document.documentElement.style.setProperty('--app-chrome-h', `${Math.max(height, 0)}px`);
    }

    measureChromeHeight();
    window.addEventListener('resize', measureChromeHeight);
    return () => {
      window.removeEventListener('resize', measureChromeHeight);
      document.documentElement.style.removeProperty('--app-chrome-h');
    };
  }, [isAdmin]);

  if (settings['__empty']) {
    return (
      <div className="product-list-wrapper">
        <h1 className="page-title">Admin Guide</h1>
        <Loading isLoading={true} />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="admin-guide" dangerouslySetInnerHTML={{ __html: GUIDE_HTML }} />
  );
}
